/**
 * 400 (#1354, mock-up "Promo prices"): the words and figures the app shows for a car's time-of-day promos. The
 * platform publishes each promo on the car's rate (`pricing.promos`, backend `cars/pricing-summary.ts`) and the quote
 * reports what each one changed (`breakdown`, backend `pricing/engine.ts`). Display only: the quote stays the price.
 * Times are Malaysia time, as staff wrote them on the rate card.
 */
export interface Promo {
  name: string;
  /** `HH:MM`; a start later than the end crosses midnight (399). */
  start_time: string;
  end_time: string;
  /** null = every day; otherwise 0 (Sunday) – 6 (Saturday). */
  days_of_week: number[] | null;
  per_hour_minor: number;
}

export interface QuoteLine {
  kind: string;
  label?: string;
  condition_type?: string;
  delta_minor: number;
}

/** `00:00` → 12am, `10:00` → 10am, `13:30` → 1:30pm. */
export function clockWords(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const suffix = h < 12 ? 'am' : 'pm';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}${m ? `:${String(m).padStart(2, '0')}` : ''}${suffix}`;
}

export function promoHoursWords(p: Pick<Promo, 'start_time' | 'end_time'>): string {
  return `${clockWords(p.start_time)}–${clockWords(p.end_time)}`;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Monday first, Sunday last, as a week is read here. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function daysWords(days: number[] | null): string {
  if (!days || days.length === 0 || days.length === 7) return 'every day';
  return WEEK_ORDER.filter((d) => days.includes(d))
    .map((d) => DAY_NAMES[d])
    .join(', ');
}

const sameDays = (a: number[] | null, b: number[] | null) =>
  JSON.stringify(a ? [...a].sort() : null) === JSON.stringify(b ? [...b].sort() : null);

/**
 * The card's one line: the lowest promo price and its hours. Promos at that price on the same days whose hours touch
 * (one ends where the next starts, midnight included) read as one stretch: Night 23:00–00:00 + Morning 00:00–10:00 is
 * 11pm–10am. Null when the car has no promo.
 */
export function promoChip(
  promos: Promo[] | null | undefined,
): { per_hour_minor: number; hours: string } | null {
  if (!promos || promos.length === 0) return null;
  const lowest = Math.min(...promos.map((p) => p.per_hour_minor));
  const cheapest = promos.filter((p) => p.per_hour_minor === lowest);
  // Begin the stretch at a promo nothing else at this price runs into; follow the chain from there.
  const first =
    cheapest.find(
      (p) =>
        !cheapest.some(
          (q) => q !== p && q.end_time === p.start_time && sameDays(q.days_of_week, p.days_of_week),
        ),
    ) ?? cheapest[0];
  if (!first) return null;
  let end = first.end_time;
  const used = new Set<Promo>([first]);
  for (;;) {
    const next = cheapest.find(
      (q) => !used.has(q) && q.start_time === end && sameDays(q.days_of_week, first.days_of_week),
    );
    if (!next) break;
    used.add(next);
    end = next.end_time;
  }
  return {
    per_hour_minor: lowest,
    hours: promoHoursWords({ start_time: first.start_time, end_time: end }),
  };
}

/** What each time-of-day promo saved in a quote (its reductions summed over split segments), in order. */
export function promoSavings(
  breakdown: QuoteLine[] | null | undefined,
): Array<{ name: string; saved_minor: number }> {
  const saved = new Map<string, number>();
  for (const line of breakdown ?? []) {
    if (line.kind !== 'rule' || line.condition_type !== 'time_of_day' || !line.label) continue;
    if (line.delta_minor >= 0) continue;
    saved.set(line.label, (saved.get(line.label) ?? 0) - line.delta_minor);
  }
  return [...saved].map(([name, saved_minor]) => ({ name, saved_minor }));
}
